// Pure derivations for the `teams` page, moved verbatim out of Teams.tsx.

import { getImageUrl } from '@/services/api'
import type { TenantDivision } from '@/contexts/LeagueContext'
import type { Team } from '@/services/api/types'
import { deriveTeamCode, getTeamDisplay } from '@/utils/teamDisplay'
import { divisionRef } from '../../division'
import type { TeamCard, TeamGroup } from './types'

/** Today's client-side search: name, short code or city contains the query (case-insensitive). */
export function filterTeams(teams: Team[], searchQuery: string): Team[] {
  return teams.filter((team) => {
    if (!searchQuery) return true
    const query = searchQuery.toLowerCase()
    return (
      team.name.toLowerCase().includes(query) ||
      team.short_code?.toLowerCase().includes(query) ||
      team.city?.toLowerCase().includes(query)
    )
  })
}

export function teamCard(team: Team): TeamCard {
  return {
    id: team.id,
    href: `/teams/${team.id}`,
    name: getTeamDisplay(team, 'full'),
    code: team.short_code || deriveTeamCode(team.name),
    shortCode: team.short_code || null,
    logoUrl: getImageUrl(team.logo_url),
    color: team.primary_color || null,
    city: team.city || null,
    raw: team,
  }
}

/**
 * #583: cards grouped under the live divisions in admin order, by each team's
 * `division_ids` (`/public/teams?division=all`). A team in two divisions is in
 * both groups; teams in none come last (division null). Empty groups are dropped.
 */
export function groupTeamsByDivision(cards: TeamCard[], live: readonly TenantDivision[]): TeamGroup[] {
  const groups: TeamGroup[] = live.map((d) => ({ division: divisionRef(live, d.id), items: [] }))
  const none: TeamCard[] = []
  for (const c of cards) {
    const ids = c.raw.division_ids ?? []
    let placed = false
    groups.forEach((g, i) => { if (ids.includes(live[i].id)) { g.items.push(c); placed = true } })
    if (!placed) none.push(c)
  }
  const out = groups.filter((g) => g.items.length > 0)
  return none.length ? [...out, { division: null, items: none }] : out
}
